import { SharedModule } from '@/app/shared/shared.module';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  GroupedDeviceInfo,
  GroupedPlantationViewModel,
  LandProxy,
  PlantationProxy,
  PlantationViewModelResponseResult,
} from '@/app/proxy/proxies';
import { ExtendedLandViewModel, PlantationService } from '@/app/core/services/plantation.service';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CropMonitoringComponent } from '@/app/features/services/crop-monitoring/crop-monitoring.component';
import { PermissionService } from '@/app/core/services/permission.service';
import { UserService } from '@/app/core/services/user.service';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

interface Menu {
  label: string;
  route: string;
  show: boolean;
  permissionKeys: string[];
}
@Component({
  selector: 'app-services',
  imports: [SharedModule, CropMonitoringComponent],
  templateUrl: './services.component.html',
  styleUrl: './services.component.less',
  providers: [PlantationProxy, LandProxy],
})
export class ServicesComponent implements OnInit, OnDestroy {
  menus: Menu[] = [];
  originlMenus: Menu[] = [
    {
      label: 'doktar_app.services.crop_monitoring',
      route: 'crop-monitoring',
      show: false,
      permissionKeys: ['CropHealth_Get', 'IrrigationProgram_Get'],
    },
    {
      label: 'doktar_app.common.irrigation',
      route: 'irrigation',
      show: false,
      permissionKeys: ['IrrigationProgram_Get'],
    },
    {
      label: 'doktar_app.services.weather_and_pest',
      route: 'weather-pest',
      show: false,
      permissionKeys: ['WeatherSoilPest_Get', 'Weathers_Get'],
    },
    {
      label: 'doktar_app.common.analysis',
      route: 'analysis',
      show: false,
      permissionKeys: ['Analysis_Get'],
    },
    {
      label: 'doktar_app.services.crop_nutrition',
      route: 'crop-nutrition',
      show: false,
      permissionKeys: ['Vra_Get'],
    },
    {
      label: 'doktar_app.common.activities',
      route: 'activityLogs',
      show: false,
      permissionKeys: ['Activity_Get'],
    },
  ];
  dialogVisible = false;
  selectedPlantation: GroupedPlantationViewModel;
  routePlantationNotFound = false;
  csiPlantationLoading = false;

  private destroy$ = new Subject<void>();
  private syncingFromRoute = false;
  isCSIDomain = window.location.hostname.includes('sustainability.doktar');

  constructor(
    public plantationService: PlantationService,
    private _router: Router,
    public _activatedRoute: ActivatedRoute,
    private _location: Location,
    private _permissionService: PermissionService,
    private _userService: UserService,
    private _landProxy: LandProxy,
    private _plantationProxy: PlantationProxy
  ) {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    if (queryParams?.['source'] === 'csi' && !this.plantationService.selectedPlantation) {
      this.csiPlantationLoading = true;
      this.plantationService.showLoading();
    }

    this.plantationService.selectedPlantation$.pipe(takeUntil(this.destroy$)).subscribe((plantation) => {
      this.selectedPlantation = plantation;

      // Keep the address bar in sync with the selected plantation so links like
      // /services/:plantationId/irrigation are shareable and survive refresh.
      // We use Location.replaceState (not router.navigate) because crossing from
      // /services/... to /services/:id/... would remount the whole services tree.
      // Skip while syncingFromRoute is set to avoid a loop with route-param selection.
      if (!this.isCSIDomain && plantation && !this.syncingFromRoute && this.isOnServicesRoute()) {
        this.writePlantationIdToUrl(plantation.id);
      }
    });
  }

  ngOnInit(): void {
    this._userService
      .loadUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.isSuccess) {
            for (const menu of this.originlMenus) {
              const isPermitted = menu.permissionKeys.some((key) =>
                this._permissionService.isGrantedOnUserPermissions(key)
              );
              if (isPermitted) {
                this.menus.push(menu);
              }
            }
          }
        },
      });

    // ----- CSI-sepecific ------
    const queryParams = this._activatedRoute.snapshot.queryParams;
    if (queryParams?.['source'] === 'csi') {
      this.setLandPlantation();
    }

    if (!this.isCSIDomain) {
      this.setupRoutePlantationSync();
    }
  }

  navigateToMenu(route: string) {
    const plantationId = this.selectedPlantation?.id;
    if (plantationId) {
      this._router.navigate(['/services', plantationId, route], { queryParamsHandling: 'merge' });
      return;
    }

    this._router.navigate([route], { relativeTo: this._activatedRoute, queryParamsHandling: 'merge' });
  }

  isRouteActivated(route: string) {
    return this._router.url.includes(route);
  }

  showSelectPlantationPanel() {
    if (window.location.hostname.includes('sustainability.doktar')) {
      return;
    }
    this.plantationService.showSelectPlantationPanel();
  }

  setLandPlantation() {
    // prevent re-fetching if we already have a selected plantation
    if (this.plantationService.selectedPlantation) {
      this.selectedPlantation = this.plantationService.selectedPlantation;
      this.csiPlantationLoading = false;
      return;
    }

    const queryParams: Params = this._activatedRoute.snapshot.queryParams;
    const landId = queryParams['landId'];
    const plantationId = queryParams['plantationId'];
    if (!landId || !plantationId) {
      this.csiPlantationLoading = false;
      if (
        window.location.hostname.includes('sustainability.doktar')
      ) {
        this._router.navigateByUrl('/csi');
      }
      return;
    }

    this.csiPlantationLoading = true;
    this.plantationService.showLoading();
    forkJoin([this._landProxy.land_Get(landId), this.getPlantationById(plantationId)])
      .pipe(
        finalize(() => {
          this.csiPlantationLoading = false;
          this.plantationService.hideLoading();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([landRes, selectPlantation]) => {
        if (landRes && selectPlantation) {
          const land: ExtendedLandViewModel = {
            id: landRes.result.id,
            name: landRes.result.name,
            enterpriseId: landRes.result.enterpriseId,
            plantations: [selectPlantation],
          };
          this.plantationService.setLandList([land]);
          this.plantationService.setSelectedPlantation(selectPlantation);
          this.selectedPlantation = selectPlantation;
          this.plantationService.setSelectedLand(land.id);
        } else {
          if (
            window.location.hostname.includes('sustainability.doktar')
          ) {
            this._router.navigateByUrl('/csi');
          }
        }
      });
  }

  getPlantationById(plantationId: string) {
    return this._plantationProxy.plantation_Get(plantationId).pipe(
      map((res: PlantationViewModelResponseResult) => {
        if (res.isSuccess) {
          const plantationDetail = res.result;
          const selectPlantation = Object.assign(new GroupedPlantationViewModel(), {
            ...plantationDetail,
            canAddCrop: plantationDetail.canAddCrop,
            canHarvest: plantationDetail.canHarvest,
            createdDate: plantationDetail.createdAt,
            offSet: plantationDetail.timeZone?.offset ?? plantationDetail.timeZone,
            activeRotationId: plantationDetail.activeRotationId,
            hasRotation: plantationDetail.hasRotation,
            boundaries: plantationDetail.boundaries.map((e) => {
              const boundary = [e.longitude, e.latitude];
              return boundary;
            }),
            devices: plantationDetail.devices.map((e) => {
              const device = new GroupedDeviceInfo();
              device.accessKey = e.id;
              device.assetId = e.assetId;
              device.deviceLineCode = e.lineCode;
              device.deviceModelCode = e.model;
              device.location = e.location;
              device.name = e.name;
              device.productionId = e.plantationId;
              device.status = e.inventoryStatus;
              return device;
            }),
          });
          return selectPlantation;
        }
        return null;
      })
    );
  }

  /**
   * Keeps the selected plantation in sync with shareable URLs like
   * /services/{plantationId}/irrigation.
   *
   * The plantation id comes from the parent route in app.routes.ts. When it
   * changes, we look it up in the user's loaded plantations and select it.
   * If the id is missing from the list, routePlantationNotFound is set so the
   * template can show the unavailable-plantation message.
   */
  private setupRoutePlantationSync(): void {
    this._activatedRoute.params
      .pipe(
        map((params) => params['plantationId'] as string | undefined),
        distinctUntilChanged(),
        switchMap((plantationId) => {
          if (!plantationId) {
            return of(undefined);
          }

          return this.waitForPlantationData().pipe(map(() => plantationId));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((plantationId) => {
        if (!plantationId) {
          this.routePlantationNotFound = false;
          return;
        }

        this.applyRoutePlantation(plantationId);
      });
  }

  /**
   * A shareable link can load before the user's plantations are fetched.
   * Do not match the URL id until plantations are in memory, or until loading
   * completes with an empty list (so we can show "plantation unavailable").
   */
  private waitForPlantationData() {
    if (this.plantationService.landList?.length > 0) {
      return of(void 0);
    }

    return combineLatest([
      this.plantationService.landList$,
      this.plantationService.loading$,
    ]).pipe(
      filter(([list, loading]) => list.length > 0 || loading === false),
      take(1),
      map((): void => undefined)
    );
  }

  /**
   * Match the plantation id from the URL against plantations the user already has.
   * If found, select it for the services tabs; if not, clear the current selection
   * and set routePlantationNotFound so the unavailable-plantation message shows.
   */
  private applyRoutePlantation(plantationId: string): void {
    for (const land of this.plantationService.landList ?? []) {
      const plantation = land.plantations?.find((p) => p.id === plantationId);
      if (plantation) {
        this.syncingFromRoute = true;
        this.routePlantationNotFound = false;
        this.plantationService.setSelectedLand(land.id);
        this.plantationService.setSelectedPlantation(plantation);
        this.selectedPlantation = plantation;
        this.syncingFromRoute = false;
        return;
      }
    }

    this.syncingFromRoute = true;
    this.routePlantationNotFound = true;
    this.plantationService.setSelectedPlantation(null);
    this.selectedPlantation = null;
    this.syncingFromRoute = false;
  }

  /** Only rewrite the URL while the user is on a services page. */
  private isOnServicesRoute(): boolean {
    return this._router.url.split('?')[0].startsWith('/services');
  }

  /**
   * Plantation → URL: inject the selected id into /services/:plantationId/:subRoute
   * without router.navigate, so legacy links (/services/irrigation) self-heal and
   * panel changes stay shareable without remounting child components.
   */
  private writePlantationIdToUrl(plantationId: string): void {
    const [path, query] = this._router.url.split('?');
    const segments = path.split('/').filter(Boolean);
    const routeId = this._activatedRoute.snapshot.params['plantationId'];
    const rest = segments.slice(routeId && segments[1] === routeId ? 2 : 1);
    const nextUrl = ['/services', plantationId, ...rest].join('/') + (query ? `?${query}` : '');

    if (nextUrl !== this._router.url) {
      this._location.replaceState(nextUrl);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (window.location.hostname.includes('sustainability.doktar')) {
      this.plantationService.reset();
    }
  }
}
